import { useCallback, useEffect, useState } from "react";

import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { z } from "zod";

import type { Patients } from "api/schemas/Patients";
import { getStorageValue, setStorageValue } from "utils/storage";

import { TherapistAssignmentRole } from "../queries/TherapistAssignmentRole";

import { getTreatmentId } from "./stringifyJoint";

const filters = [
  "active",
  "inactive",
  "invited",
  "deleted",
  "new_patients",
  "new_comments",
  "new_messages",
  "appointment_to_schedule",
  "prio",
  "week",
  "primary_joint",
  "selfcare",
  "guided_care",
  TherapistAssignmentRole.MainTherapist,
  TherapistAssignmentRole.SubstituteTherapist,
  "awaiting_treatment_ready",
  "hybrid_care",
] as const;

export type Filter = typeof filters[number];

const FilterSchema = z.object({
  active: z.boolean(),
  from: z.number().optional(),
  to: z.number().optional(),
  selection: z.array(z.string()).optional(),
});

export const PatientsListFiltersSchema = z.object(
  Object.fromEntries(filters.map(f => [f, FilterSchema])) as Record<Filter, typeof FilterSchema>
);

type FilterState = z.infer<typeof FilterSchema>;
const initialState: z.infer<typeof PatientsListFiltersSchema> = {
  active: { active: false },
  inactive: { active: false },
  invited: { active: false },
  deleted: { active: false },
  new_patients: { active: false },
  new_comments: { active: false },
  new_messages: { active: false },
  appointment_to_schedule: { active: false },
  prio: { active: false },
  week: {
    active: false,
    from: 1,
    to: 3,
  },
  primary_joint: {
    active: false,
    selection: [],
  },
  main_therapist: { active: false },
  substitute_therapist: { active: false },
  selfcare: { active: false },
  guided_care: { active: false },
  awaiting_treatment_ready: { active: false },
  hybrid_care: { active: false },
};

const isFilter = (val: string | string[] | null): val is Filter => {
  if (typeof val === "string") {
    return filters.includes(val as Filter);
  }
  return false;
};

export type Filters = {
  activeFilters: Filter[];
  clearAll: () => void;
  hasFilters: () => boolean;
  filterLabel: (f: Filter) => string;
  getFilterState: (key: Filter) => FilterState;
  setFilter: (key: Filter, state: FilterState) => void;
  toggleFilter: (f: Filter) => void;
  matchesActiveFilters: (p: Patients) => boolean;
};

export const useFilters = (): Filters => {
  const storedState = getStorageValue("patient_list_filters", true);
  const [filterStates, setFilterStates] = useState(storedState || initialState);
  const location = useLocation();
  const { filter: queryFilter } = queryString.parse(location.search);
  const { t } = useTranslation();
  const activeFilters = filters.filter(f => filterStates[f].active);

  const handleFilterStatesUpdate = useCallback((newStates: Record<Filter, FilterState>) => {
    setFilterStates(newStates);
    setStorageValue("patient_list_filters", newStates, true);
  }, []);

  useEffect(() => {
    if (isFilter(queryFilter)) {
      handleFilterStatesUpdate({
        ...initialState,
        [queryFilter]: {
          ...initialState[queryFilter],
          active: true,
        },
      });
    }
  }, [handleFilterStatesUpdate, queryFilter]);

  return {
    activeFilters,

    hasFilters: () => activeFilters.length > 0,

    clearAll: () => handleFilterStatesUpdate(initialState),

    filterLabel: (filter: Filter) => {
      switch (filter) {
        case "week": {
          const { from, to } = filterStates[filter];
          if (from === 0 && to === 0) return t(`patients.introduction`);
          if (from === to) return `${t("patients.week")} ${from}`;
          if (to && to < 100) return `${t("patients.week")} ${from} to ${to}`;
          return `${t("patients.week")} ${from} ${t("patients.and_up")}`;
        }
        case "active":
        case "inactive":
        case "invited":
        case "deleted":
        case "awaiting_treatment_ready":
          return t(`patients.statuses.${filter}`);
        case "primary_joint":
          return t("patients.ailment");
        case "hybrid_care":
          return t("patients.hybrid_care");
        default:
          return t(`patients.${filter}`);
      }
    },

    getFilterState: (key: Filter) => filterStates[key],

    setFilter: (key: Filter, state: FilterState) => {
      handleFilterStatesUpdate({ ...filterStates, [key]: { ...filterStates[key], ...state } });
    },

    toggleFilter: (key: Filter) => {
      handleFilterStatesUpdate({ ...filterStates, [key]: { ...filterStates[key], active: !filterStates[key].active } });
    },

    matchesActiveFilters: (patient: Patients) => {
      return activeFilters.every(filter => {
        switch (filter) {
          case "active":
            return (
              patient.status === "active" ||
              (activeFilters.includes("inactive") && patient.status === "inactive") ||
              activeFilters.includes("deleted")
            );
          case "inactive":
            return (
              patient.status === "inactive" ||
              (activeFilters.includes("active") && patient.status === "active") ||
              activeFilters.includes("deleted")
            );
          case "invited":
            return patient.status === "invited";
          case "deleted":
            return patient.deleted === true;
          case "week":
            return (
              (filterStates.week.from !== undefined ? patient.week >= filterStates.week.from : true) &&
              (filterStates.week.to !== undefined ? patient.week <= filterStates.week.to : true)
            );
          case "new_patients":
            return patient.new_patient;
          case "appointment_to_schedule":
            return patient.appointment_to_schedule;
          case "new_comments":
            return patient.new_comments;
          case "new_messages":
            return patient.new_messages;
          case "prio":
            return patient.prio;
          case "primary_joint": {
            const treatmentId = getTreatmentId({
              ailment: patient.ailment,
              joint: patient.primary_joint || { location: null, lateral_location: null },
            });

            return filterStates.primary_joint.selection?.includes(treatmentId);
          }
          case TherapistAssignmentRole.MainTherapist:
            return (
              patient.therapist_assignment_role === TherapistAssignmentRole.MainTherapist ||
              (patient.therapist_assignment_role === TherapistAssignmentRole.SubstituteTherapist &&
                activeFilters.includes(TherapistAssignmentRole.SubstituteTherapist))
            );
          case TherapistAssignmentRole.SubstituteTherapist:
            return (
              patient.therapist_assignment_role === TherapistAssignmentRole.SubstituteTherapist ||
              (patient.therapist_assignment_role === TherapistAssignmentRole.MainTherapist &&
                activeFilters.includes(TherapistAssignmentRole.MainTherapist))
            );
          case "selfcare":
            return (
              patient.premium_type === "selfcare" ||
              (activeFilters.includes("guided_care") && patient.premium_type === "guided_care")
            );
          case "guided_care":
            return (
              patient.premium_type === "guided_care" ||
              (activeFilters.includes("selfcare") && patient.premium_type === "selfcare")
            );
          case "awaiting_treatment_ready":
            return patient.awaiting_treatment_ready;
          case "hybrid_care":
            return patient.ce_structure === "hybrid";
          default:
            return true;
        }
      });
    },
  };
};
